import type { FunctionComponent } from 'react';
import { ContentBrick } from '../../../app-kit/components/ContentBrick/ContentBrick';
import { DisclaimerIcon } from '../../../app-kit/components/DisclaimerIcon/DisclaimerIcon';
import { useSuburbPageModalContext } from '../../contexts/SuburbPageModalContext/SuburbPageModalContext';
import { trackDisclaimerModalOpen } from '../../../analytics/event-tracking/trackDisclaimerModalOpen';
import { styled } from 'styled-components';
import { mediaHelper, spacingHelper, typographyHelper } from '@property.com.au/ui-kit';
import { TextWithStyles } from '../../../app-kit/components/DynamicText/DynamicText';
import { DynamicContent } from '../../../app-kit/components/DynamicContent/DynamicContent';
import { DynamicContentsItems } from '../../../app-kit/components/DynamicContent/types';
import flatmatesLogoSvg from '../../../assets/flatmates.svg';
import { SubBrick } from './subcomponents/SubBrick/SubBrick';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingHelper('extraSmall')};

  ${mediaHelper('desktopMin')} {
    flex-direction: row;
  }
`;

const FooterNote = styled.div`
  margin-top: ${spacingHelper('extraSmall')};

  & > * {
    ${typographyHelper('body02')};
  }
`;

const FlatmatesLogo = styled.img`
  margin-top: ${spacingHelper('medium')};
`;

interface SuburbPageFlatmatesBrickProps {
  title: string;
  subtitle: string;
  averageRoomRentSubBrick: {
    title: string;
    value: string;
    unit?: string;
    description: TextWithStyles[];
  };
  supplyAndDemandSubBrick: {
    title: string;
    value: string;
    description: TextWithStyles[];
  };
  footerNote: DynamicContentsItems;
}

export const SuburbPageFlatmatesBrick: FunctionComponent<SuburbPageFlatmatesBrickProps> = ({
  title,
  subtitle,
  averageRoomRentSubBrick,
  supplyAndDemandSubBrick,
  footerNote,
}) => {
  const { displayModal } = useSuburbPageModalContext();

  return (
    <ContentBrick
      title={title}
      subtitle={subtitle}
      headerChildren={
        <DisclaimerIcon
          title="Open Flatmates Data Disclaimer"
          onClick={() => {
            displayModal({ name: 'flatmatesDataDisclaimer' });
            trackDisclaimerModalOpen({
              inPageSource: 'suburb_page__flatmates_brick___disclaimer_button',
            });
          }}
        />
      }
    >
      <Content>
        <SubBrick
          title={averageRoomRentSubBrick.title}
          value={averageRoomRentSubBrick.value}
          unit={averageRoomRentSubBrick.unit}
          description={averageRoomRentSubBrick.description}
        />
        <SubBrick
          title={supplyAndDemandSubBrick.title}
          value={supplyAndDemandSubBrick.value}
          description={supplyAndDemandSubBrick.description}
        />
      </Content>
      <FooterNote>
        <DynamicContent dynamicContents={footerNote} textColorVariant="textSecondary" />
      </FooterNote>
      <FlatmatesLogo src={flatmatesLogoSvg} alt="Flatmates Logo" />
    </ContentBrick>
  );
};
